import { ApiClient } from '../api-client';
import {
  deviceAssessmentValidateEvaluationIdUrl,
  deviceAssessmentResultsUrl,
  deviceImageUploadsListUrl,
  deviceImageUploadUrl,
  doneUploadingImageUrl,
} from './urls';

import {
  ICosmeticsAssessmentApiOptions,
  IDeviceAssessmentResultsPayload,
  IDeviceAssessmentResultsResponseDataRaw,
  IDeviceImageListGradeProResponseDataRaw,
  IDeviceImageListResponseDataRaw,
  IDeviceImageUploadOptions,
  IDeviceImageUploadPayload,
  IDeviceImageUploadResponseData,
  IDoneUploadingDeviceImagesPayload,
  IDoneUploadingDeviceImagesResponseData,
  IInvalidDeviceAssessmentResponseDataRaw,
  IValidateDeviceAssessmentResponseDataRaw,
} from './types';

import {
  formatDeviceImageUploadsListApple,
  formatDeviceImageUploadsListGradePro,
  formatInvalidDeviceAssessmentResponse,
  formatSubmitDeviceAssessmentResultsResponse,
  formatValidateDeviceAssessmentResponse,
} from './formatters';

/**
 * This is provides the API client methods for all user authentication,
 * authorization and identity services like login, forgot password etc.
 *
 * NOTE: The `refreshToken` service has been moved out as a global to avoid
 * cyclic dependencies since its to be consumed within the base `ApiClient`.
 *
 */
export class CosmeticEvaluationClient extends ApiClient {
  private static classInstance?: CosmeticEvaluationClient;

  baseURL = process.env.NEXT_PUBLIC_DIAGNOSTICS_BASE_URL;

  private constructor() {
    super({
      baseURL: process.env.NEXT_PUBLIC_DIAGNOSTICS_BASE_URL,
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new CosmeticEvaluationClient();
    }

    return this.classInstance;
  };

  /**
   * This fetches the list of required images to be uploaded later to the apple
   * diagnostic servers. With a key, token, url for each image.
   */
  public fetchDeviceImageUploadsList = async ({
    assessmentType,
    baseURL,
    sessionId,
  }: ICosmeticsAssessmentApiOptions) => {
    const response = await this.get<
      IDeviceImageListResponseDataRaw | IDeviceImageListGradeProResponseDataRaw
    >(deviceImageUploadsListUrl({ sessionId, assessmentType }), {
      baseURL: baseURL || this.baseURL,
      requiresAuth: false,
    });

    if (!response.success) throw response;

    if (assessmentType === 'APPLE') {
      // TODO: Tell @Jihin to add common keys to both responses.
      // @ts-ignore
      return formatDeviceImageUploadsListApple(response.data?.data);
    }

    // TODO: Tell @Jihin to add common keys to both responses.
    // @ts-ignore
    return formatDeviceImageUploadsListGradePro(response.data?.data);
  };

  /**
   * This uploads the captured device image to the servers.
   * Mainly expects the image file the image color id as `imageId` and the
   * sessionid as `id`.
   */
  public uploadDeviceImage = async ({
    assessmentType,
    baseURL,
    id,
    imageId,
    file,
  }: IDeviceImageUploadOptions) => {
    const response = await this.post<
      IDeviceImageUploadResponseData,
      Omit<IDeviceImageUploadPayload, 'baseURL'>
    >(
      deviceImageUploadUrl(),
      { assessmentType, id, imageId, file },
      {
        baseURL: baseURL || this.baseURL,
        requiresAuth: false,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1 * 60 * 1000, // Force a 1 minute wait on uploads.
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * This API verifies with the backend that all the images have been completed
   * for server to kill this session and have the user proceed to the Retail App.
   */
  public doneUploadingImage = async (sessionId: string, baseURL?: string) => {
    const response = await this.post<
      IDoneUploadingDeviceImagesResponseData,
      IDoneUploadingDeviceImagesPayload
    >(
      doneUploadingImageUrl(),
      { id: sessionId, status: 'COMPLETED' },
      {
        baseURL: baseURL || this.baseURL,
        requiresAuth: false,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * FUNCTIONAL ASSESSMENT
   * Gets validates the provided EII number from the retail app assessment that
   * was passed via the URL param eii.
   */
  public validateDeviceAssessmentSession = async (
    evaluationId: string,
    baseURL: string
  ) => {
    const response = await this.get<
      IValidateDeviceAssessmentResponseDataRaw,
      unknown,
      IInvalidDeviceAssessmentResponseDataRaw
    >(deviceAssessmentValidateEvaluationIdUrl(evaluationId), {
      baseURL: baseURL || this.baseURL,
      requiresAuth: false,
      headers: {
        'x-api-key': process.env.NEXT_PUBLIC_X_API_KEY,
      },
    });

    if (!response.success) {
      return formatInvalidDeviceAssessmentResponse(
        response.error?.response?.data?.data
      );
    }

    return formatValidateDeviceAssessmentResponse(response.data.data);
  };

  /**
   * FUNCTIONAL ASSESSMENT
   * This publishes the functional assessment test results to the server which
   * then communicates to the retail app.
   */
  public submitDeviceAssessmentResults = async ({
    baseURL,
    ...payload
  }: IDeviceAssessmentResultsPayload) => {
    const response = await this.post<
      IDeviceAssessmentResultsResponseDataRaw,
      Omit<IDeviceAssessmentResultsPayload, 'baseURL'>
    >(deviceAssessmentResultsUrl(), payload, {
      baseURL: baseURL || this.baseURL,
      requiresAuth: false,
      headers: {
        'x-api-key': process.env.NEXT_PUBLIC_X_API_KEY,
      },
    });

    if (!response.success) throw response;

    return formatSubmitDeviceAssessmentResultsResponse(response.data.data);
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for cosmetic evaluations related requests.
 */
export const COSMETIC_EVALUATION_API =
  CosmeticEvaluationClient.getClientInstance();
